import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
    MdArrowBack,
    MdSearch,
    MdLocationOn,
    MdExpandMore,
    MdExpandLess,
    MdStore,
    MdFileDownload,
    MdTrendingUp,
    MdFilterList,
    MdCurrencyRupee,
    MdDescription
} from 'react-icons/md';
import styles from './VendorPOSummary.module.css';
import Loader from '../components/common/Loader';
import NoData from '../components/common/NoData';
import Pagination from '../components/common/Pagination';
import TableLimitSelector from '../components/common/TableLimitSelector';
import Drawer from '../components/common/Drawer';
import PaginatedSelect from '../components/common/PaginatedSelect';
import { authFetch } from '../components/utils/authFetch';
import {
    GET_VENDOR_PO_SUMMARY_API,
    GENERATE_PO_PDF_API,
    BASE_API,
    GET_ALL_PROJECT_API
} from '../config/API';
import Swal from 'sweetalert2';
import axios from 'axios';

const VendorPOSummary = () => {
    const { vendorId } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const [vendorDetails, setVendorDetails] = useState(null);
    const [summary, setSummary] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const handleLimitChange = (newLimit) => {
        setLimit(newLimit);
        setCurrentPage(1);
    };
    const [totalPages, setTotalPages] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');

    const [showFilters, setShowFilters] = useState(false);
    const [dateRange, setDateRange] = useState({ start: '', end: '' });
    const [selectedProject, setSelectedProject] = useState(null);

    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [showStats, setShowStats] = useState(window.innerWidth > 768);

    useEffect(() => {
        const handleResize = () => {
            const mobile = window.innerWidth <= 768;
            setIsMobile(mobile);
            if (!mobile) setShowStats(true);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchTerm);
        }, 500);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    const fetchPOSummary = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('accessToken');
            let queryParams = `?page=${currentPage}&limit=${limit}&search=${debouncedSearch}`;

            if (dateRange.start) queryParams += `&startDate=${dateRange.start}`;
            if (dateRange.end) queryParams += `&endDate=${dateRange.end}`;
            if (selectedProject) queryParams += `&projectId=${selectedProject.value}`;

            const response = await authFetch(
                `${GET_VENDOR_PO_SUMMARY_API(vendorId)}${queryParams}`,
                {
                    headers: { Authorization: `Bearer ${token}` }
                }
            );
            const result = await response.json();

            if (result.status) {
                setData(result.data || []);
                setVendorDetails(result.vendorDetails);
                setSummary(result.summary);
                const pagination = result.pagination;
                setTotalPages(pagination?.totalPages || 1);
                setTotalRecords(pagination?.totalCount || result.data?.length || 0);
            } else {
                setData([]);
                setVendorDetails(null);
                setSummary(null);
                setTotalPages(1);
                setTotalRecords(0);
            }
        } catch (error) {
            console.error('Error fetching vendor PO summary:', error);
            setData([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchPOSummary();
    }, [vendorId, currentPage, debouncedSearch, dateRange, selectedProject, limit]);

    const formatCurrency = (amount) => {
        const val = parseFloat(amount || 0);
        return `₹${val.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    const handleExportCSV = async () => {
        try {
            const token = localStorage.getItem('accessToken');
            let queryParams = `?exportCSV=true&search=${debouncedSearch}`;

            if (dateRange.start) queryParams += `&startDate=${dateRange.start}`;
            if (dateRange.end) queryParams += `&endDate=${dateRange.end}`;
            if (selectedProject?.value) queryParams += `&projectId=${selectedProject.value}`;

            const response = await authFetch(
                `${GET_VENDOR_PO_SUMMARY_API(vendorId)}${queryParams}`,
                {
                    headers: { Authorization: `Bearer ${token}` }
                }
            );
            const result = await response.json();

            if (result.status) {
                toast.success(result.message || 'Export started! Check Report Log for the file.');
            } else {
                toast.error(result.message || 'Failed to start export');
            }
        } catch (error) {
            console.error('Error exporting CSV:', error);
            toast.error('An error occurred during export');
        }
    };

    const handleGeneratePDF = async (poId) => {
        Swal.fire({
            title: 'Generating PDF...',
            text: 'Please wait, generating your Purchase Order PDF.',
            allowOutsideClick: false,
            didOpen: () => {
                Swal.showLoading();
            }
        });

        try {
            const token = localStorage.getItem('accessToken');
            const response = await axios.get(GENERATE_PO_PDF_API(poId), {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.status && response.data.pdf) {
                const fullPdfUrl = `${BASE_API}/${response.data.pdf}`;
                const fileResponse = await axios.get(fullPdfUrl, {
                    headers: { Authorization: `Bearer ${token}` },
                    responseType: 'blob'
                });

                const fileURL = URL.createObjectURL(new Blob([fileResponse.data]));
                const link = document.createElement('a');
                link.href = fileURL;
                link.setAttribute('download', `PO_${poId}.pdf`);
                document.body.appendChild(link);
                link.click();
                link.parentNode.removeChild(link);
                URL.revokeObjectURL(fileURL);

                Swal.close();
            } else {
                throw new Error(response.data.message || 'Failed to generate PDF URL');
            }
        } catch (error) {
            console.error('Error generating PDF:', error);
            Swal.fire('Error', 'Failed to generate PDF. Please try again.', 'error');
        }
    };

    const getStatusClass = (status) => {
        const s = status?.toLowerCase();
        if (s === 'pending') return styles['status-pending'];
        if (s === 'completed' || s === 'active') return styles['status-completed'];
        if (s === 'cancelled') return styles['status-cancelled'];
        if (s === 'partial') return styles['status-partial'];
        return '';
    };

    return (
        <div className={styles['purchase-orders-container']}>
            {/* Header Section */}
            <div className={styles['purchase-orders-header']}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <button className={styles['back-btn']} onClick={() => window.history.length > 1 ? navigate(-1) : window.close()}>
                        <MdArrowBack size={20} />
                    </button>
                    <div>
                        <h1>PO Summary • {vendorDetails?.vendorName || 'Vendor'}</h1>
                        <div className={styles['page-subtitle']}>
                            <MdStore /> {vendorDetails?.companyName}
                            {vendorDetails?.category && (
                                <span className={styles['vendor-category']}>{vendorDetails.category}</span>
                            )}
                        </div>
                    </div>
                </div>
                <div className={styles['header-actions']}>
                    <button
                        className={`${styles['filter-btn']} ${showFilters ? styles.active : ''}`}
                        onClick={() => setShowFilters(!showFilters)}
                    >
                        <MdFilterList /> Filter
                    </button>
                    <button
                        className={styles['export-btn']}
                        onClick={handleExportCSV}
                    >
                        <MdFileDownload size={18} /> Export CSV
                    </button>
                </div>
            </div>

            <Drawer
                isOpen={showFilters}
                onClose={() => setShowFilters(false)}
                title="Filter Reports"
            >
                <div className={styles['drawer-filter-content']}>
                    <div className={styles['filter-item']}>
                        <label>Project</label>
                        <PaginatedSelect
                            apiUrl={GET_ALL_PROJECT_API}
                            mapResponse={(p) => ({
                                value: p.id || p._id,
                                label: p.name || p.projectName
                            })}
                            value={selectedProject}
                            onChange={(option) => setSelectedProject(option)}
                            placeholder="Select Project"
                            isClearable
                        />
                    </div>
                    <div className={styles['filter-item']}>
                        <label>Start Date</label>
                        <input
                            type="date"
                            value={dateRange.start}
                            onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                        />
                    </div>
                    <div className={styles['filter-item']}>
                        <label>End Date</label>
                        <input
                            type="date"
                            value={dateRange.end}
                            onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                        />
                    </div>
                    <div className={styles['drawer-actions']}>
                        <button
                            className={styles['clear-filters-btn']}
                            onClick={() => {
                                setDateRange({ start: '', end: '' });
                                setSelectedProject(null);
                            }}
                            style={{ width: '100%' }}
                        >
                            Reset Filter
                        </button>
                    </div>
                </div>
            </Drawer>

            {/* Mobile Stats Toggle */}
            {isMobile && (
                <button
                    className={styles['stats-toggle-btn']}
                    onClick={() => setShowStats(!showStats)}
                >
                    {showStats ? <MdExpandLess /> : <MdExpandMore />}
                    {showStats ? 'Hide Summary' : 'Show Summary'}
                </button>
            )}

            {/* Stats Grid */}
            <div className={`${styles['stats-grid']} ${showStats ? styles.show : styles.hide}`}>
                <div className={styles['stat-card']}>
                    <div className={styles['stat-header']}>
                        <div className={`${styles['icon-box']} ${styles['purple']}`}>
                            <MdDescription />
                        </div>
                    </div>
                    <span className={styles['stat-label']}>Total POs</span>
                    <span className={styles['stat-value']}>{summary?.totalPOs || 0}</span>
                </div>
                <div className={styles['stat-card']}>
                    <div className={styles['stat-header']}>
                        <div className={`${styles['icon-box']} ${styles['blue']}`}>
                            <MdCurrencyRupee />
                        </div>
                    </div>
                    <span className={styles['stat-label']}>Total Amount</span>
                    <span className={`${styles['stat-value']} ${styles['amount-cell-grandTotal']}`}>
                        {formatCurrency(summary?.totalAmount)}
                    </span>
                </div>
                <div className={styles['stat-card']}>
                    <div className={styles['stat-header']}>
                        <div className={`${styles['icon-box']} ${styles['green']}`}>
                            <MdCurrencyRupee />
                        </div>
                    </div>
                    <span className={styles['stat-label']}>Paid Amount</span>
                    <span className={`${styles['stat-value']} ${styles['amount-cell-paid']}`}>
                        {formatCurrency(summary?.totalPaidAmount)}
                    </span>
                </div>
                <div className={styles['stat-card']}>
                    <div className={styles['stat-header']}>
                        <div className={`${styles['icon-box']} ${styles['red']}`}>
                            <MdCurrencyRupee />
                        </div>
                    </div>
                    <span className={styles['stat-label']}>Remaining</span>
                    <span className={`${styles['stat-value']} ${styles['amount-cell-remaining']}`}>
                        {formatCurrency(summary?.totalRemainingAmount)}
                    </span>
                </div>
            </div>

            {/* Main Content Card */}
            <div className={styles['card-container-po']}>
                <div className={styles['search-container']}>
                    <TableLimitSelector limit={limit} onLimitChange={handleLimitChange} />
                    <div className={styles['search-wrapper']}>
                        <MdSearch className={styles['search-icon']} />
                        <input
                            type="text"
                            placeholder="Search PO number or project..."
                            className={styles['search-input']}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                </div>

                {!isMobile ? (
                    <div className={styles['table-container']}>
                        <table>
                            <thead>
                                <tr>
                                    <th>PO Number</th>
                                    <th>Project Details</th>
                                    <th>Date</th>
                                    <th className={styles['text-center']}>Item Details</th>
                                    <th>Status</th>
                                    <th className={styles['text-right']}>Total Amount</th>
                                    <th className={styles['text-right']}>Paid Amount</th>
                                    <th className={styles['text-right']}>Remaining</th>
                                    <th className={styles['text-center']}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr>
                                        <td colSpan="9" style={{ textAlign: 'center', padding: '3rem' }}>
                                            <Loader text="Fetching PO list..." />
                                        </td>
                                    </tr>
                                ) : data.length === 0 ? (
                                    <tr>
                                        <td colSpan="9" style={{ textAlign: 'center', padding: '3rem' }}>
                                            <NoData title="No Purchase Orders" message="No PO data available for this vendor." />
                                        </td>
                                    </tr>
                                ) : (
                                    data.map((po) => (
                                        <tr key={po.poId}>
                                            <td className={styles['po-number-cell']}>
                                                <span
                                                    className={styles['link-text']}
                                                    onClick={() => window.open(`/reports/po/details/${po.poId}`, '_blank')}
                                                >
                                                    {po.poNumber}
                                                </span>
                                            </td>
                                            <td>
                                                <div className={styles['vendor-info']}>
                                                    <div className={styles['vendor-name']}>{po.projectName}</div>
                                                    <div className={styles['vendor-meta']}>
                                                        <MdLocationOn size={12} /> {po.projectAdd}
                                                    </div>
                                                </div>
                                            </td>
                                            <td>{po.poDate}</td>
                                            <td className={styles['text-center']}>
                                                <div className={styles['items-cell-container']}>
                                                    <span
                                                        className={styles['link-text']}
                                                        onClick={() => window.open(`/reports/po/details/${po.poId}`, '_blank')}
                                                    >
                                                        {po.totalProducts || 0} Items
                                                    </span>
                                                    {po.products?.length > 0 && (
                                                        <div className={styles['items-tooltip']}>
                                                            {po.products.map((prod, i) => (
                                                                <span key={i} className={styles['item-badge']}>
                                                                    {prod.productName}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                            <td>
                                                <span className={`${styles['status-badge']} ${getStatusClass(po.poStatus)}`}>
                                                    {po.poStatus}
                                                </span>
                                            </td>
                                            <td className={`${styles['text-right']} ${styles['amount-cell-grandTotal']}`}>{formatCurrency(po.totalAmount)}</td>
                                            <td className={`${styles['text-right']} ${styles['amount-cell-paid']}`}>{formatCurrency(po.totalPaidAmount)}</td>
                                            <td className={`${styles['text-right']} ${styles['amount-cell-remaining']}`}>{formatCurrency(po.totalRemainingAmount)}</td>
                                            <td className={styles['text-center']}>
                                                <button
                                                    className={styles['action-btn']}
                                                    onClick={() => handleGeneratePDF(po.poId)}
                                                    title="Download PO PDF"
                                                >
                                                    <MdFileDownload size={20} />
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className={styles['mobile-card-view']}>
                        {loading ? (
                            <div style={{ padding: '2rem' }}>
                                <Loader text="Fetching POs..." />
                            </div>
                        ) : data.length === 0 ? (
                            <NoData title="No Purchase Orders" message="Try adjusting your search." />
                        ) : (
                            data.map((po) => (
                                <div key={po.poId} className={styles['project-card']}>
                                    <div className={styles['card-header']}>
                                        <span className={styles['po-badge']}>#{po.poNumber}</span>
                                        <span className={`${styles['status-badge']} ${getStatusClass(po.poStatus)}`}>
                                            {po.poStatus}
                                        </span>
                                    </div>
                                    <div className={styles['card-body']}>
                                        <div style={{ fontWeight: '600', color: '#111827', marginBottom: '4px' }}>{po.projectName}</div>
                                        <div className={styles['card-row']} style={{ marginBottom: '8px' }}>
                                            <span style={{ fontSize: '12px', color: '#6b7280', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                <MdLocationOn size={14} /> {po.projectAdd}
                                            </span>
                                            <span style={{ fontSize: '12px', color: '#6b7280' }}>{po.poDate}</span>
                                        </div>
                                        <div className={styles['card-row']}>
                                            <span className={styles['card-label']}>Items:</span>
                                            <span
                                                className={`${styles['card-value']} ${styles['link-text']}`}
                                                onClick={() => window.open(`/reports/po/details/${po.poId}`, '_blank')}
                                            >
                                                {po.totalProducts || 0} Items
                                            </span>
                                        </div>
                                        <div className={styles['card-row']}>
                                            <span className={styles['card-label']}>Total Amount:</span>
                                            <span className={`${styles['card-value']} ${styles['amount-cell-grandTotal']}`}>{formatCurrency(po.totalAmount)}</span>
                                        </div>
                                        <div className={styles['card-row']}>
                                            <span className={styles['card-label']}>Paid Amount:</span>
                                            <span className={`${styles['card-value']} ${styles['amount-cell-paid']}`}>{formatCurrency(po.totalPaidAmount)}</span>
                                        </div>
                                        <div className={styles['card-row']}>
                                            <span className={styles['card-label']}>Remaining:</span>
                                            <span className={`${styles['card-value']} ${styles['amount-cell-remaining']}`}>{formatCurrency(po.totalRemainingAmount)}</span>
                                        </div>
                                        <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #f3f4f6' }}>
                                            <button
                                                className={styles['primary-btn']}
                                                onClick={() => handleGeneratePDF(po.poId)}
                                                style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '10px', background: '#f3f4f6', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '14px', fontWeight: '600', color: '#374151', cursor: 'pointer' }}
                                            >
                                                <MdFileDownload size={18} /> Download PDF
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                )}

                <Pagination limit={limit} onLimitChange={handleLimitChange}
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalRecords={totalRecords}
                    onPageChange={(page) => setCurrentPage(page)}
                    currentCount={data.length}
                    entityName="PO"
                />
            </div>
        </div>
    );
};

export default VendorPOSummary;
